import { COLORS } from '@/styles/tokens/colors';

/**
 * Type styles for presentation slides, named to match the Figma
 * "Presentation/*" type styles. Defined here as plain SX objects
 * (rather than promoted to global Typography variants) because they
 * differ from the existing project variants — for example, the global
 * `eyebrow` variant is 10px/16px medium, while the presentation
 * eyebrow is 12px/18px semibold. If the design system reconciles
 * these, the constants below are the obvious factor-out point for
 * promotion to shared variants.
 *
 * Two consumers:
 *  - Slides applying a style to a static element (e.g. a `<p>` that
 *    isn't editable) import a style directly and spread it via `sx`.
 *  - `PresentationMarkdownField` consumes these via its `variant`
 *    prop, applying them to its internal `<p>` so the markdown picks
 *    them up in both read-only and editing modes.
 */
export const PRESENTATION_TYPE_STYLES = {
  /** "Presentation/BodyS" — Inter Regular 14/20. Applied by
   *  `PresentationMarkdownField` when no other variant is set so the
   *  field matches the Figma body spec. Without this, fields inherit
   *  the slide's `PresentationTypographyProvider` size (16/22) and
   *  the bumped global `text-primary` weight (medium / 500). Variants
   *  whose type style specifies a different size or weight (e.g.
   *  `eyebrow`, `titleM`) override these baseline values. */
  default: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
  },

  /** "Presentation/TitleM" — Inter Semibold 16/24, neutral-gray-900. */
  titleM: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600,
    color: COLORS.NEUTRAL_GRAY[900],
  },

  /** "Presentation/TitleS" — body size at semibold weight, for the
   *  primary line of a header/body pair (e.g. a document name above a
   *  date, a person's name above a relationship). Size inherits from
   *  the `default` baseline. */
  titleS: {
    fontWeight: 600,
  },

  /** "Presentation/Subtitle" — body size at gray-500 for secondary
   *  text under a heavier title (e.g. "Logan Roy" / "Brother"). Just a
   *  color shift; size/weight inherit from the `default` baseline. */
  subtitle: {
    color: COLORS.TEXT.SECONDARY,
  },

  /** "Presentation/eyebrowBold" — Inter Semibold 12/18, 1px letter-
   *  spacing, uppercase. */
  eyebrow: {
    fontSize: 12,
    lineHeight: '18px',
    letterSpacing: '1px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
  },
} as const;
